import React, { useState } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { IdGen, Pane, Label, NumericInput, Text, Selector, TabPane, TextInput } from '@leverege/ui-elements';
import { GeoPointEditor, LineStyleSelector, CapSelector, JoinSelector } from '@leverege/molten/data-viewers';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { Colorizers } from '@leverege/ui-attributes';
import Model from "./ZoneVehicleModel.js";
import S from './Grid.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SHOW_ACCURACY_ON_OPTS = [{
  value: null,
  name: 'Never'
}, {
  value: 'selected',
  name: 'Selected'
}, {
  value: 'rollover',
  name: 'Rollover'
}, {
  value: 'always',
  name: 'Always'
}];
function ZoneVehicleEditor(props) {
  const {
    value,
    objectType
  } = props;
  const onModelChange = useValueChange(Model, props);
  const [ids] = useState(IdGen.createIds('attr', 15));
  let cid = 0;
  if (value == null) {
    return null;
  }
  const showAccuracy = Model.getShowAccuracyOn(value);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: "Point",
      tabId: "point",
      children: /*#__PURE__*/_jsx(GeoPointEditor, {
        ...props
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: "BLE Spread",
      tabId: "spread",
      children: /*#__PURE__*/_jsxs("div", {
        className: classnames(S.sub, S.fill, S.grid),
        children: [/*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[cid],
          children: "Radius %"
        }), /*#__PURE__*/_jsx(NumericInput, {
          id: ids[cid++],
          eventData: "setSpreadRadius",
          min: 1,
          step: 10,
          max: 100,
          allowError: true,
          value: Model.getSpreadRadius(value),
          onChange: onModelChange
        }, `haz-${Model.getSpreadRadius(value) || 'n'}`)]
      })
    }), /*#__PURE__*/_jsxs(TabPane.Tab, {
      tabName: "Accuracy",
      tabId: "accuracy",
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.fill,
        variant: "subtitle",
        children: "Accuracy"
      }), /*#__PURE__*/_jsxs("div", {
        className: classnames(S.sub, S.fill, S.grid),
        children: [/*#__PURE__*/_jsx(Label, {
          children: "Show Accuracy On"
        }), /*#__PURE__*/_jsx(Selector, {
          value: showAccuracy,
          onChange: onModelChange,
          eventData: "setShowAccuracyOn",
          values: SHOW_ACCURACY_ON_OPTS
        })]
      }), showAccuracy != null && /*#__PURE__*/_jsx(React.Fragment, {
        children: /*#__PURE__*/_jsxs(Pane, {
          variant: "editor",
          className: S.grid,
          children: [/*#__PURE__*/_jsx(Text, {
            className: S.fill,
            variant: "subtitle",
            children: "Fill"
          }), /*#__PURE__*/_jsxs(Pane, {
            variant: "editor",
            className: classnames(S.sub, S.fill, S.grid),
            children: [/*#__PURE__*/_jsx(Label, {
              htmlFor: ids[cid],
              children: "Color"
            }), ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                id: ids[cid++]
              },
              modelKey: 'colorizer',
              values: Colorizers.getColorizersFor(objectType),
              value: Model.getFillColor(value),
              eventData: 'setFillColor',
              onChange: onModelChange
            }, S.fill), /*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Opacity"
            }), /*#__PURE__*/_jsx(NumericInput, {
              id: ids[cid++],
              min: 0,
              max: 1,
              float: true,
              eventData: "setFillOpacity",
              allowError: true,
              value: Model.getFillOpacity(value),
              onChange: onModelChange
            }), /*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Pattern"
            }), /*#__PURE__*/_jsx(TextInput, {
              id: ids[cid++],
              eventData: "setFillPattern",
              allowError: true,
              value: Model.getFillPattern(value),
              onChange: onModelChange
            })]
          }), /*#__PURE__*/_jsx(Text, {
            className: S.fill,
            variant: "subtitle",
            children: "Outline"
          }), /*#__PURE__*/_jsxs(Pane, {
            variant: "editor",
            className: classnames(S.sub, S.fill, S.grid),
            children: [/*#__PURE__*/_jsx(Label, {
              htmlFor: ids[cid],
              children: "Width"
            }), /*#__PURE__*/_jsx(NumericInput, {
              id: ids[cid++],
              min: 0,
              max: 50,
              float: true,
              eventData: "setLineWidth",
              allowError: true,
              value: Model.getLineWidth(value),
              onChange: onModelChange
            }), /*#__PURE__*/_jsx(Label, {
              htmlFor: ids[cid],
              children: "Color"
            }), ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                id: ids[cid++]
              },
              modelKey: 'colorizer',
              values: Colorizers.getColorizersFor(objectType),
              value: Model.getLineColor(value),
              eventData: 'setLineColor',
              onChange: onModelChange
            }, S.fill), /*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Opacity"
            }), /*#__PURE__*/_jsx(NumericInput, {
              id: ids[cid++],
              min: 0,
              max: 1,
              float: true,
              eventData: "setLineOpacity",
              allowError: true,
              value: Model.getLineOpacity(value),
              onChange: onModelChange
            }), /*#__PURE__*/_jsx(Label, {
              htmlFor: ids[cid],
              children: "Style"
            }), /*#__PURE__*/_jsx(LineStyleSelector, {
              id: ids[cid++],
              value: Model.getLineStyle(value),
              eventData: "setLineStyle",
              onChange: onModelChange
            }), Model.getLineStyle(value) === 'pattern' && /*#__PURE__*/_jsxs(React.Fragment, {
              children: [/*#__PURE__*/_jsx(Label, {
                variant: "input",
                htmlFor: ids[cid],
                children: "Pattern"
              }), /*#__PURE__*/_jsx(TextInput, {
                id: ids[cid++],
                eventData: "setLinePattern",
                allowError: true,
                value: Model.getLinePattern(value),
                onChange: onModelChange
              })]
            }), /*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Cap"
            }), /*#__PURE__*/_jsx(CapSelector, {
              eventData: "setLineCap",
              value: Model.getLineCap(value) || 'butt',
              onChange: onModelChange
            }), /*#__PURE__*/_jsx(Label, {
              variant: "input",
              htmlFor: ids[cid],
              children: "Join"
            }), /*#__PURE__*/_jsx(JoinSelector, {
              eventData: "setLineJoin",
              value: Model.getLineJoin(value) || 'miter',
              onChange: onModelChange
            })]
          })]
        })
      })]
    })]
  });
}
export default /*#__PURE__*/React.memo(ZoneVehicleEditor);